import {
	IonBackButton,
	IonButtons,
	IonHeader,
	IonIcon,
	IonPage,
	IonText,
	IonTitle,
	IonToolbar,
	useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { usePostHog } from "posthog-js/react";
import z from "zod";
import { AuthSubmitButton } from "@/components/Auth/SubmitButton";
import { FormInput } from "@/components/Form/Input";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { backendService } from "@/lib/backend";
import { type ShareManifestDto } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { Toast } from "@/lib/capacitor/Toast";
import { arrowLeftIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { checkCircleIcon, crossCircleIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { envelopeIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { Routes } from "@/lib/router";
import { Sentry } from "@/lib/sentry";
import { logger } from "@/logger";

export function ShareManifest() {
	const posthog = usePostHog();
	const { t } = useTranslation();
	const router = useIonRouter();

	const mutationShareManifest = useExtractedMutation({
		mutationKey: ["manifest", "share"],
		mutationFn: (variables: ShareManifestDto) =>
			backendService.manifest.manifestControllerShare(variables),
	});

	const form = useForm({
		validators: {
			onSubmit: z.object({
				email: z.string().email(t("fields.email.errors.invalid")),
			}),
		},
		defaultValues: {
			email: "",
		},
		onSubmit: async ({ value }) => {
			await mutationShareManifest.mutateAsync(value, {
				onSuccess: () => {
					posthog.capture("Share Manifest", { success: true });
					Toast.show({ text: t("page.share-manifest.success") });
					form.reset();
					router.push(Routes.Dashboard(), "forward");
				},
				onError: (error) => {
					Sentry.captureException(error);
					posthog.capture("Share Manifest", { success: false });
					logger.getLogger("Share Manifest").error("Share Manifest", { error });
				},
			});
		},
	});

	return (
		<IonPage>
			<IonHeader className="ion-no-border">
				<Toolbar className="ion-p-2 ion-pb-2 ion-pt-6 ion-min-h-16 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
					<IonButtons slot="start">
						<IonBackButton
							data-attr="page.share-manifest.back"
							data-touch-target
							defaultHref={Routes.Dashboard()}
							color="light"
							className="part-icon:m-0 part-icon:size-5 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-brown-300 part-native:p-1 part-native:text-xs part-native:text-brown-600 min-h-0"
							icon={arrowLeftIcon}
							text=""
						/>
					</IonButtons>
				</Toolbar>
			</IonHeader>
			<Content className="part-scroll:gap-6 flex flex-col gap-6">
				<section className="flex flex-col gap-2">
					<IonHeader collapse="condense" className="-mt-(--padding-top)">
						<IonToolbar className="ion-px-0">
							<IonTitle className="ion-text-brown-700 p-0 text-start text-3xl font-bold">
								{t("widgets.share-manifest.title")}
							</IonTitle>
						</IonToolbar>
					</IonHeader>
					<IonText className="text-gray-600">{t("page.share-manifest.description")}</IonText>
				</section>

				<form
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						form.handleSubmit();
					}}
					className="flex flex-col gap-6"
				>
					<form.Field name="email">
						{(field) => (
							<FormInput
								data-attr="page.share-manifest.email"
								field={field}
								type="email"
								label={t("fields.email.label")}
								autocomplete="email"
								inputMode="email"
								placeholder={t("fields.email.placeholder")}
							>
								<IonIcon slot="start" icon={envelopeIcon} aria-hidden="true" className="size-5" />
							</FormInput>
						)}
					</form.Field>

					<section className="flex flex-col gap-2">
						<div className="border-brown-300 flex flex-col gap-2 rounded-2xl border-2 p-4">
							<IonText className="text-brown-700 font-semibold">
								{t("page.share-manifest.what-is-shared.title")}
							</IonText>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={checkCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-shared.core-values")}
								</IonText>
							</div>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={checkCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-shared.reflection")}
								</IonText>
							</div>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={checkCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-shared.friend-check")}
								</IonText>
							</div>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={checkCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-shared.perfect-day")}
								</IonText>
							</div>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={checkCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-shared.your-mission")}
								</IonText>
							</div>
						</div>

						<div className="border-brown-300 flex flex-col gap-2 rounded-2xl border-2 p-4">
							<IonText className="text-brown-700 font-semibold">
								{t("page.share-manifest.what-is-not-shared.title")}
							</IonText>
							<div className="flex items-center gap-2">
								<IonIcon
									slot="start"
									icon={crossCircleIcon}
									className="text-brown-400 size-5 flex-none"
								/>
								<IonText className="text-brown-600">
									{t("page.share-manifest.what-is-not-shared.chats")}
								</IonText>
							</div>
						</div>
					</section>

					<AuthSubmitButton
						data-attr="page.share-manifest.submit"
						isSubmitting={form.state.isSubmitting}
					>
						{t("page.share-manifest.submit")}
					</AuthSubmitButton>
				</form>
			</Content>
		</IonPage>
	);
}
